import React, { useEffect, useRef, useState } from "react";

import { categoriesService } from "api/services/categories.service";
import { productsService } from "api/services/products.service";
import Button from "components/UI/Button";
import { Select } from "components/UI/Input";
import RichTextEditor from "components/UI/RichTextEditor";

import type { Category } from "api/types/category.types";
import type {
  ShippingPolicy,
  ReturnExchangePolicy,
  AfterServicePolicy,
} from "api/types/product.types";
import "./ProductRegistration.scss";

// ─── Types ───────────────────────────────────────────────────────────────────
type DescType = "editor" | "image";

interface OptionValue {
  id: number;
  value: string;
  price_delta: number;
}

interface OptionGroup {
  id: number;
  name: string;
  values: OptionValue[];
}

interface Policies {
  shipping_policies: ShippingPolicy[];
  return_exchange_policies: ReturnExchangePolicy[];
  after_service_policies: AfterServicePolicy[];
}

// ─── 섹션 헤더 ────────────────────────────────────────────────────────────────
const SectionTitle: React.FC<{ title: string; description?: string }> = ({
  title,
  description,
}) => (
  <div className="pr-section__title">
    <h3>{title}</h3>
    {description && <p>{description}</p>}
  </div>
);

// ─── 폼 필드 래퍼 ─────────────────────────────────────────────────────────────
const Field: React.FC<{
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, required, hint, children }) => (
  <div className="pr-field">
    <div className="pr-field__label">
      {label}
      {required && <span className="pr-field__required">*</span>}
    </div>
    <div className="pr-field__control">
      {children}
      {hint && <p className="pr-field__hint">{hint}</p>}
    </div>
  </div>
);

// ─── 라디오 ───────────────────────────────────────────────────────────────────
const RadioGroup: React.FC<{
  name: string;
  value: string;
  options: { value: string; label: string; description?: string }[];
  onChange: (v: string) => void;
}> = ({ name, value, options, onChange }) => (
  <div className="pr-radio-group">
    {options.map((opt) => (
      <label
        key={opt.value}
        className={`pr-radio ${value === opt.value ? "pr-radio--active" : ""}`}
      >
        <input
          type="radio"
          name={name}
          value={opt.value}
          checked={value === opt.value}
          onChange={() => onChange(opt.value)}
        />
        <span className="pr-radio__dot" />
        <span className="pr-radio__label">{opt.label}</span>
        {opt.description && <span className="pr-radio__desc">{opt.description}</span>}
      </label>
    ))}
  </div>
);

// ─── 메인 컴포넌트 ─────────────────────────────────────────────────────────────
const ProductRegistration: React.FC = () => {
  // 기본 정보
  const [productName, setProductName] = useState("");
  const [brand, setBrand] = useState("");
  const [cat1, setCat1] = useState<number | "">("");
  const [cat2, setCat2] = useState<number | "">("");
  const [cat3, setCat3] = useState<number | "">("");

  // 가격
  const [price, setPrice] = useState("");
  const [discountPrice, setDiscountPrice] = useState("");
  const [discountRate, setDiscountRate] = useState(0);

  // 재고
  const [stock, setStock] = useState("");
  const [isUnlimitedStock, setIsUnlimitedStock] = useState(false);
  const [lowStockThreshold, setLowStockThreshold] = useState(5);

  // 상태
  const [isActive, setIsActive] = useState(true);
  const [isFeatured, setIsFeatured] = useState(false);
  const [isDigital, setIsDigital] = useState(false);

  // 설명
  const [descType, setDescType] = useState<DescType>("editor");
  const [description, setDescription] = useState("");

  // 태그
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");

  // 이미지
  const imageInputRef = useRef<HTMLInputElement>(null);
  const descImageInputRef = useRef<HTMLInputElement>(null);
  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [descImages, setDescImages] = useState<File[]>([]);
  const [descImagePreviews, setDescImagePreviews] = useState<string[]>([]);

  // 옵션
  const [hasOptions, setHasOptions] = useState(false);
  const [options, setOptions] = useState<OptionGroup[]>([]);

  // 사용자 입력 옵션 (구매자가 직접 입력하는 항목의 제목 목록)
  const [customerInputOptions, setCustomerInputOptions] = useState<string[]>([]);

  // 정책
  const [policies, setPolicies] = useState<Policies>({
    shipping_policies: [],
    return_exchange_policies: [],
    after_service_policies: [],
  });
  const [policiesLoading, setPoliciesLoading] = useState(true);
  const [shippingPolicyId, setShippingPolicyId] = useState<number | null>(null);
  const [returnPolicyId, setReturnPolicyId] = useState<number | null>(null);
  const [afterServiceId, setAfterServiceId] = useState<number | null>(null);

  // 카테고리
  const [allCategories, setAllCategories] = useState<Category[]>([]);

  // 제출
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // ─── 카테고리 로드 ──────────────────────────────────────────────────────────
  useEffect(() => {
    categoriesService
      .listAll()
      .then((res) => {
        const data = res.data;
        setAllCategories(Array.isArray(data) ? data : (data.results ?? []));
      })
      .catch(() => {
        /* ignore */
      });
  }, []);

  // ─── 정책 로드 ──────────────────────────────────────────────────────────────
  useEffect(() => {
    setPoliciesLoading(true);
    productsService
      .getPolicyOptions()
      .then((res) => {
        setPolicies(res.data);
      })
      .catch(() => {
        console.error("정책 목록을 불러오는데 실패했습니다.");
      })
      .finally(() => {
        setPoliciesLoading(false);
      });
  }, []);

  // API가 트리 구조로 반환: 최상위 배열 = depth0, children = depth1
  const depth0 = allCategories ?? [];
  const depth1: Category[] = depth0.find((c) => c.id === cat1)?.children ?? [];
  const depth2: Category[] = depth1.find((c) => c.id === cat2)?.children ?? [];

  // ─── 할인율 자동 계산 ───────────────────────────────────────────────────────
  useEffect(() => {
    const p = Number(price);
    const dp = Number(discountPrice);
    if (p > 0 && dp >= 0) {
      setDiscountRate(Math.max(0, Math.round(((p - dp) / p) * 100)));
    } else {
      setDiscountRate(0);
    }
  }, [price, discountPrice]);

  // ─── 이미지 핸들러 ──────────────────────────────────────────────────────────
  const handleAddImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const combined = [...images, ...files].slice(0, 10);
    setImages(combined);
    setImagePreviews(combined.map((f) => URL.createObjectURL(f)));
    e.target.value = "";
  };

  const handleRemoveImage = (idx: number) => {
    URL.revokeObjectURL(imagePreviews[idx]);
    setImages((prev) => prev.filter((_, i) => i !== idx));
    setImagePreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  // 상품 설명 에디터 이미지 업로드 → 서버 URL 반환
  const uploadDetailImage = async (file: File): Promise<string> => {
    const { data } = await productsService.uploadImage(file);
    return data.url;
  };

  const handleAddDescImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const combined = [...descImages, ...files].slice(0, 10);
    setDescImages(combined);
    setDescImagePreviews(combined.map((f) => URL.createObjectURL(f)));
    e.target.value = "";
  };

  const handleRemoveDescImage = (idx: number) => {
    URL.revokeObjectURL(descImagePreviews[idx]);
    setDescImages((prev) => prev.filter((_, i) => i !== idx));
    setDescImagePreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  // ─── 태그 핸들러 ────────────────────────────────────────────────────────────
  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (!trimmed || tags.includes(trimmed)) return;
    setTags((prev) => [...prev, trimmed]);

    setTagInput("");
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags((prev) => prev.filter((t) => t !== tag));
  };

  // ─── 사용자 입력 옵션 핸들러 ─────────────────────────────────────────────────
  const addCustomerInputOption = () => {
    setCustomerInputOptions((prev) => [...prev, ""]);
  };

  const updateCustomerInputOption = (index: number, value: string) => {
    setCustomerInputOptions((prev) => prev.map((t, i) => (i === index ? value : t)));
  };

  const removeCustomerInputOption = (index: number) => {
    setCustomerInputOptions((prev) => prev.filter((_, i) => i !== index));
  };

  // ─── 옵션 핸들러 ────────────────────────────────────────────────────────────
  const addOption = () => {
    setOptions((prev) => [
      ...prev,
      { id: Date.now(), name: "", values: [{ id: Date.now() + 1, value: "", price_delta: 0 }] },
    ]);
  };

  const removeOption = (id: number) => {
    setOptions((prev) => prev.filter((o) => o.id !== id));
  };

  const updateOptionName = (id: number, name: string) => {
    setOptions((prev) => prev.map((o) => (o.id === id ? { ...o, name } : o)));
  };

  const addOptionValue = (optId: number) => {
    setOptions((prev) =>
      prev.map((o) =>
        o.id === optId
          ? { ...o, values: [...o.values, { id: Date.now(), value: "", price_delta: 0 }] }
          : o,
      ),
    );
  };

  const removeOptionValue = (optId: number, valId: number) => {
    setOptions((prev) =>
      prev.map((o) =>
        o.id === optId ? { ...o, values: o.values.filter((v) => v.id !== valId) } : o,
      ),
    );
  };

  const updateOptionValue = (
    optId: number,
    valId: number,
    field: "value" | "price_delta",
    val: string | number,
  ) => {
    setOptions((prev) =>
      prev.map((o) =>
        o.id === optId
          ? { ...o, values: o.values.map((v) => (v.id === valId ? { ...v, [field]: val } : v)) }
          : o,
      ),
    );
  };

  // ─── 제출 ──────────────────────────────────────────────────────────────────
  const handleSubmit = async (isDraft = false) => {
    if (!productName) {
      setSubmitError("상품명을 입력해주세요.");
      return;
    }
    if (!price || Number(price) <= 0) {
      setSubmitError("정가를 입력해주세요.");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      // 이미지 입력 방식일 경우 설명 이미지 업로드 후 URL을 detailed_content로 변환
      let detailedContent = description;
      if (descType === "image" && descImages.length > 0) {
        const uploadResults = await Promise.all(
          descImages.map((f) => productsService.uploadImage(f)),
        );
        detailedContent = uploadResults.map((res) => `<img src="${res.data.url}" />`).join("");
      }

      // 가장 깊이 선택된 카테고리 id만 전송
      const leafCatId = (cat3 || cat2 || cat1) as number | "";
      const categoryIds = leafCatId !== "" ? [leafCatId] : [];
      await productsService.create({
        name: productName,
        brand,
        detailed_content: detailedContent,
        price,
        discount_price: discountPrice || null,
        stock_quantity: isUnlimitedStock ? undefined : stock ? Number(stock) : 0,
        is_unlimited_stock: isUnlimitedStock,
        low_stock_threshold: lowStockThreshold,
        is_active: !isDraft,
        is_featured: isFeatured,
        is_digital: isDigital,
        // tags: [...tags],
        shipping_policy_id: shippingPolicyId,
        return_exchange_policy_id: returnPolicyId,
        after_service_id: afterServiceId,
        category_ids: categoryIds,
        options:
          hasOptions && options.length > 0
            ? options.map((o) => ({
                name: o.name,
                values: o.values.map((v) => ({ value: v.value, price_delta: v.price_delta })),
              }))
            : undefined,
        customer_input_options: customerInputOptions.map((t) => t.trim()).filter((t) => t !== ""),
        images: images.length > 0 ? images : undefined,
        image_thumbnails: images.map((_, i) => i === 0),
      });
      alert(isDraft ? "임시저장 완료!" : "상품이 등록되었습니다!");
    } catch {
      setSubmitError("상품 등록 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="product-registration">
      {/* ── 상단 헤더 ─────────────────────────────────────────── */}
      <div className="pr-header">
        <div className="pr-header__left">
          <h1 className="pr-header__title">{productName || "새 상품 등록"}</h1>
          <p className="pr-header__breadcrumb">상품 관리 › 상품 등록</p>
        </div>
        <div className="pr-header__actions">
          <Button variant="outline" disabled={submitting}>
            취소
          </Button>
          {/* <Button variant='outline' disabled={submitting} onClick={() => handleSubmit(true)}>임시저장</Button> */}
          <Button variant="primary" disabled={submitting} onClick={() => handleSubmit(false)}>
            {submitting ? "처리 중..." : "등록하기"}
          </Button>
        </div>
      </div>
      {submitError && (
        <p style={{ color: "#EF4444", padding: "8px 24px", margin: 0 }}>{submitError}</p>
      )}

      <div className="pr-body">
        {/* ── 기본 정보 ─────────────────────────────────────────── */}
        <section className="pr-section">
          <SectionTitle title="기본 정보" />

          <Field label="상품명" required hint="최대 40자까지 입력 가능합니다.">
            <div className="pr-input-with-count">
              <input
                className="pr-input"
                placeholder="상품명을 입력해주세요"
                value={productName}
                maxLength={40}
                onChange={(e) => setProductName(e.target.value)}
              />
              <span className="pr-input-count">{productName.length}/40</span>
            </div>
          </Field>

          <Field label="브랜드">
            <input
              className="pr-input"
              placeholder="브랜드명을 입력해주세요"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
            />
          </Field>

          <Field label="카테고리" required>
            <div className="pr-category">
              <Select
                // value={String(cat1)}
                onChange={(e) => {
                  setCat1(e.target.value ? Number(e.target.value) : "");
                  setCat2("");
                  setCat3("");
                }}
                options={[
                  { value: "", label: "대분류 선택" },
                  ...depth0.map((c) => ({ value: String(c.id), label: c.name })),
                ]}
                style={{ flex: 1 }}
              />
              <Select
                value={String(cat2)}
                onChange={(e) => {
                  setCat2(e.target.value ? Number(e.target.value) : "");
                  setCat3("");
                }}
                disabled={!cat1}
                options={[
                  { value: "", label: "중분류 선택" },
                  ...depth1.map((c) => ({ value: String(c.id), label: c.name })),
                ]}
                style={{ flex: 1 }}
              />
              {depth2.length > 0 && (
                <Select
                  value={String(cat3)}
                  onChange={(e) => setCat3(e.target.value ? Number(e.target.value) : "")}
                  disabled={!cat2}
                  options={[
                    { value: "", label: "소분류 선택" },
                    ...depth2.map((c) => ({ value: String(c.id), label: c.name })),
                  ]}
                  style={{ flex: 1 }}
                />
              )}
            </div>
          </Field>

          <Field
            label="상품 이미지"
            required
            hint="최대 10장까지 업로드 가능합니다. 첫 번째 이미지가 썸네일로 사용됩니다. 권장 크기: 1000×1000px"
          >
            <div className="pr-image-upload">
              {imagePreviews.map((src, i) => (
                <div key={i} className="pr-image-upload__item pr-image-upload__item--filled">
                  <img
                    src={src}
                    alt={`상품이미지${i + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  {i === 0 && (
                    <span
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: "rgba(99,102,241,0.85)",
                        color: "#fff",
                        fontSize: 10,
                        textAlign: "center",
                        padding: "2px 0",
                      }}
                    >
                      썸네일
                    </span>
                  )}
                  <button
                    className="pr-image-upload__remove"
                    type="button"
                    onClick={() => handleRemoveImage(i)}
                  >
                    ✕
                  </button>
                </div>
              ))}
              {images.length < 10 && (
                <div
                  className="pr-image-upload__item pr-image-upload__item--add"
                  onClick={() => imageInputRef.current?.click()}
                >
                  <span style={{ fontSize: 24, color: "#A1A1AA" }}>＋</span>
                  <span style={{ fontSize: 12, color: "#71717A", marginTop: 4 }}>이미지 추가</span>
                </div>
              )}
            </div>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={handleAddImage}
            />
          </Field>

          <Field label="상품 태그">
            <div className="pr-tag-input-row">
              <input
                className="pr-input"
                placeholder="태그 입력 후 Enter 또는 추가 버튼 클릭"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                style={{ flex: 1 }}
              />
              <Button variant="outline" size="sm" onClick={handleAddTag}>
                추가
              </Button>
            </div>
            {tags.length > 0 && (
              <div className="pr-tags" style={{ marginTop: 8 }}>
                {tags.map((tag) => (
                  <span key={tag} className="pr-tag pr-tag--selected">
                    {tag}
                    <button
                      type="button"
                      className="pr-tag__remove"
                      onClick={() => handleRemoveTag(tag)}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
          </Field>
        </section>

        {/* ── 상품 설명 ────────────────────────────────────────── */}
        <section className="pr-section">
          <SectionTitle title="상품 설명" />

          <Field label="입력 방식">
            <RadioGroup
              name="descType"
              value={descType}
              onChange={(v) => setDescType(v as DescType)}
              options={[
                { value: "editor", label: "HTML 에디터" },
                { value: "image", label: "이미지 업로드" },
              ]}
            />
          </Field>

          {descType === "editor" ? (
            <Field label="상품 설명 내용" required>
              <RichTextEditor
                value={description}
                onChange={setDescription}
                uploadImage={uploadDetailImage}
                placeholder="상품 설명을 입력해주세요..."
              />
            </Field>
          ) : (
            <Field label="설명 이미지" required hint="최대 10장까지 업로드 가능합니다.">
              <div className="pr-image-upload">
                {descImagePreviews.map((src, i) => (
                  <div key={i} className="pr-image-upload__item pr-image-upload__item--filled">
                    <img
                      src={src}
                      alt={`설명이미지${i + 1}`}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    <button
                      className="pr-image-upload__remove"
                      type="button"
                      onClick={() => handleRemoveDescImage(i)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {descImages.length < 10 && (
                  <div
                    className="pr-image-upload__item pr-image-upload__item--add"
                    onClick={() => descImageInputRef.current?.click()}
                  >
                    <span style={{ fontSize: 24, color: "#A1A1AA" }}>＋</span>
                    <span style={{ fontSize: 12, color: "#71717A", marginTop: 4 }}>
                      이미지 추가
                    </span>
                  </div>
                )}
              </div>
              <input
                ref={descImageInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={handleAddDescImage}
              />
            </Field>
          )}
        </section>

        {/* ── 가격 / 옵션 설정 ─────────────────────────────────── */}
        <section className="pr-section">
          <SectionTitle
            title="가격 / 옵션 설정"
            description='단일 상품이면 "옵션 없음"을, 색상·사이즈 등 옵션이 있다면 "옵션 있음"을 선택하세요.'
          />

          <div className="pr-price-grid">
            <Field label="정가" required hint="1,000원 이상 입력">
              <div className="pr-input-suffix">
                <input
                  className="pr-input pr-input--right"
                  type="text"
                  placeholder="0"
                  value={price ? Number(price).toLocaleString("ko-KR") : ""}
                  onChange={(e) => setPrice(e.target.value.replace(/[^0-9]/g, ""))}
                />
                <span className="pr-suffix">원</span>
              </div>
            </Field>
            <Field label="판매가" required hint="정가 이하로 입력">
              <div className="pr-input-suffix">
                <input
                  className="pr-input pr-input--right"
                  type="text"
                  placeholder="0"
                  value={discountPrice ? Number(discountPrice).toLocaleString("ko-KR") : ""}
                  onChange={(e) => setDiscountPrice(e.target.value.replace(/[^0-9]/g, ""))}
                />
                <span className="pr-suffix">원</span>
              </div>
            </Field>
            <Field label="할인율 (자동 계산)">
              <div className="pr-input-suffix">
                <input
                  className="pr-input pr-input--right"
                  type="number"
                  readOnly
                  value={discountRate}
                />
                <span className="pr-suffix">%</span>
              </div>
            </Field>
          </div>

          <Field label="옵션 구성" required>
            <div className="pr-checkbox-row">
              <label className="pr-checkbox-label">
                <input
                  type="checkbox"
                  checked={hasOptions}
                  onChange={(e) => {
                    setHasOptions(e.target.checked);
                    if (!e.target.checked) setOptions([]);
                  }}
                />
                <span>옵션 사용</span>
              </label>
            </div>
          </Field>

          {!hasOptions ? (
            /* ─ 단일 상품 재고 ─ */
            <div className="pr-price-grid">
              <Field label="재고 수량" required>
                <div className="pr-input-suffix">
                  <input
                    className="pr-input pr-input--right"
                    type="number"
                    placeholder="0"
                    value={stock}
                    onChange={(e) => setStock(e.target.value)}
                    disabled={isUnlimitedStock}
                  />
                  <span className="pr-suffix">개</span>
                </div>
              </Field>
              <Field label="무제한 재고">
                <div className="pr-checkbox-row">
                  <label className="pr-checkbox-label">
                    <input
                      type="checkbox"
                      checked={isUnlimitedStock}
                      onChange={(e) => setIsUnlimitedStock(e.target.checked)}
                    />
                    <span>무제한으로 설정</span>
                  </label>
                </div>
              </Field>
              <Field label="재고 알림 수량">
                <div className="pr-input-suffix">
                  <input
                    className="pr-input pr-input--right"
                    type="number"
                    placeholder="5"
                    value={lowStockThreshold}
                    onChange={(e) => setLowStockThreshold(Number(e.target.value))}
                  />
                  <span className="pr-suffix">개 미만</span>
                </div>
              </Field>
            </div>
          ) : (
            /* ─ 옵션 있음 ─ */
            <Field
              label="옵션 설정"
              required
              hint="옵션명(예: 색상)과 옵션값(예: 빨강)을 입력하세요."
            >
              <div className="pr-option-rows">
                {options.map((opt, optIdx) => (
                  <div key={opt.id} className="pr-option-group">
                    <div className="pr-option-group__header">
                      <span className="pr-option-row__index">옵션 {optIdx + 1}</span>
                      <input
                        className="pr-input"
                        placeholder="옵션명 (예: 색상)"
                        value={opt.name}
                        onChange={(e) => updateOptionName(opt.id, e.target.value)}
                        style={{ width: 180 }}
                      />
                      <button
                        className="pr-option-row__remove"
                        type="button"
                        onClick={() => removeOption(opt.id)}
                      >
                        ✕
                      </button>
                    </div>
                    <div className="pr-option-values">
                      {opt.values.map((val) => (
                        <div key={val.id} className="pr-option-value-row">
                          <input
                            className="pr-input"
                            placeholder="옵션값 (예: 빨강)"
                            value={val.value}
                            onChange={(e) =>
                              updateOptionValue(opt.id, val.id, "value", e.target.value)
                            }
                            style={{ flex: 1 }}
                          />
                          <div className="pr-input-suffix" style={{ width: 140 }}>
                            <input
                              className="pr-input pr-input--right"
                              type="number"
                              placeholder="0"
                              value={val.price_delta}
                              onChange={(e) =>
                                updateOptionValue(
                                  opt.id,
                                  val.id,
                                  "price_delta",
                                  Number(e.target.value),
                                )
                              }
                            />
                            <span className="pr-suffix">원</span>
                          </div>
                          {opt.values.length > 1 && (
                            <button
                              className="pr-option-row__remove"
                              type="button"
                              onClick={() => removeOptionValue(opt.id, val.id)}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                      <Button variant="secondary" size="sm" onClick={() => addOptionValue(opt.id)}>
                        + 값 추가
                      </Button>
                    </div>
                  </div>
                ))}
                <Button variant="secondary" size="sm" onClick={addOption}>
                  + 옵션 추가
                </Button>
              </div>
            </Field>
          )}

          <Field label="사용자 입력 옵션" hint="구매자가 직접 입력할 항목의 제목을 추가하세요.">
            <div className="pr-input-options">
              {customerInputOptions.map((title, index) => (
                <div className="pr-option-row" key={index}>
                  <input
                    className="pr-input"
                    placeholder="제목을 입력해주세요 (예: 각인 문구)"
                    value={title}
                    onChange={(e) => updateCustomerInputOption(index, e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <button
                    className="pr-option-row__remove"
                    type="button"
                    onClick={() => removeCustomerInputOption(index)}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <Button variant="secondary" size="sm" onClick={addCustomerInputOption}>
                + 제목 추가
              </Button>
            </div>
          </Field>
        </section>

        {/* ── 상품 상태 설정 ──────────────────────────────────── */}
        <section className="pr-section">
          <SectionTitle title="상품 상태 설정" />

          <div className="pr-price-grid">
            <Field label="판매 여부" required>
              <Select
                value={String(isActive)}
                onChange={(e) => setIsActive(e.target.value === "true")}
                options={[
                  { value: "true", label: "판매 중" },
                  { value: "false", label: "판매 중지" },
                ]}
              />
            </Field>
            <Field label="대표 상품">
              <Select
                value={String(isFeatured)}
                onChange={(e) => setIsFeatured(e.target.value === "true")}
                options={[
                  { value: "false", label: "아니오" },
                  { value: "true", label: "예" },
                ]}
              />
            </Field>
            <Field label="디지털 상품">
              <Select
                value={String(isDigital)}
                onChange={(e) => setIsDigital(e.target.value === "true")}
                options={[
                  { value: "false", label: "아니오 (실물 상품)" },
                  { value: "true", label: "예 (디지털 상품)" },
                ]}
              />
            </Field>
          </div>
        </section>

        {/* ── 정책 설정 ────────────────────────────────────────── */}
        <section className="pr-section">
          <SectionTitle title="정책 설정" />

          <div className="pr-price-grid">
            <Field label="배송 정책">
              <Select
                value={String(shippingPolicyId ?? "")}
                onChange={(e) =>
                  setShippingPolicyId(e.target.value ? Number(e.target.value) : null)
                }
                disabled={policiesLoading}
                options={[
                  { value: "", label: policiesLoading ? "불러오는 중..." : "정책 선택" },
                  ...policies.shipping_policies.map((p) => ({
                    value: String(p.id),
                    label: p.label ?? p.name,
                  })),
                ]}
              />
            </Field>
            <Field label="반품/교환 정책">
              <Select
                value={String(returnPolicyId ?? "")}
                onChange={(e) => setReturnPolicyId(e.target.value ? Number(e.target.value) : null)}
                disabled={policiesLoading}
                options={[
                  { value: "", label: policiesLoading ? "불러오는 중..." : "정책 선택" },
                  ...policies.return_exchange_policies.map((p) => ({
                    value: String(p.id),
                    label: p.label ?? p.name,
                  })),
                ]}
              />
            </Field>
            <Field label="A/S 정책">
              <Select
                value={String(afterServiceId ?? "")}
                onChange={(e) => setAfterServiceId(e.target.value ? Number(e.target.value) : null)}
                disabled={policiesLoading}
                options={[
                  { value: "", label: policiesLoading ? "불러오는 중..." : "정책 선택" },
                  ...policies.after_service_policies.map((p) => ({
                    value: String(p.id),
                    label: p.label ?? p.name,
                  })),
                ]}
              />
            </Field>
          </div>
        </section>

        {/* ── 하단 버튼 ────────────────────────────────────────── */}
        <div className="pr-footer">
          <Button variant="outline" disabled={submitting}>
            취소
          </Button>
          <div style={{ display: "flex", gap: 8 }}>
            {/* <Button variant='outline' disabled={submitting} onClick={() => handleSubmit(true)}>임시저장</Button> */}
            <Button variant="primary" disabled={submitting} onClick={() => handleSubmit(false)}>
              {submitting ? "처리 중..." : "등록하기"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductRegistration;
